<script>
/**
 * Keeps row action buttons aligned to the visible right edge of a horizontally scrollable v-data-table.
 *
 * Required in the host component:
 * - tableActionsRefName (data): v-data-table ref, e.g. 'driversTable'
 * - tableActionsOffsetActive (computed): when the table is mounted/visible
 * - tableActionsOffsetLayoutVersion (computed, optional): bump when columns/rows change (re-binds observers)
 */
export default {
  data: () => ({
    actionsOffset: {
      tableVisibleArea: 0,
      tableWidth: 0,
      scrolled: 0
    },
    tableActionsResizeObserver: null
  }),
  computed: {
    tableWrapper() {
      const refName = this.tableActionsRefName
      if (!refName) return null
      return this.$refs[refName]?.$el?.querySelector('.v-data-table__wrapper') ?? null
    },
    tableElement() {
      const refName = this.tableActionsRefName
      if (!refName) return null
      return this.$refs[refName]?.$el?.querySelector('table') ?? null
    }
  },
  watch: {
    tableActionsOffsetActive(isActive) {
      this.$nextTick(() => {
        if (isActive) {
          this.enableTableActionsOffset()
        } else {
          this.disableTableActionsOffset()
        }
      })
    },
    tableActionsOffsetLayoutVersion() {
      this.refreshTableActionsOffsetOnLayoutChange()
    }
  },
  mounted() {
    if (this.tableActionsOffsetActive) {
      this.enableTableActionsOffset()
    }
  },
  beforeDestroy() {
    this.disableTableActionsOffset()
  },
  methods: {
    enableTableActionsOffset() {
      if (!this.tableActionsOffsetActive) return

      this.$nextTick(() => {
        if (!this.tableActionsOffsetActive) return

        this.bindTableActionsScroll()
        this.setupResizeObserver()
        this.updateTableActionsOffset()
      })
    },
    disableTableActionsOffset() {
      this.unbindTableActionsScroll()
      this.teardownResizeObserver()
    },
    setupResizeObserver() {
      this.teardownResizeObserver()

      const { tableWrapper, tableElement } = this
      if (!tableWrapper) return

      if (typeof ResizeObserver === 'undefined') {
        this.updateTableActionsOffset()
        return
      }

      this.tableActionsResizeObserver = new ResizeObserver(() => {
        this.updateTableActionsOffset()
      })

      this.tableActionsResizeObserver.observe(tableWrapper)
      if (tableElement) {
        this.tableActionsResizeObserver.observe(tableElement)
      }
    },
    teardownResizeObserver() {
      if (this.tableActionsResizeObserver) {
        this.tableActionsResizeObserver.disconnect()
        this.tableActionsResizeObserver = null
      }
    },
    bindTableActionsScroll() {
      this.unbindTableActionsScroll()
      this.tableWrapper?.addEventListener('scroll', this.moveActions, { passive: true })
    },
    unbindTableActionsScroll() {
      this.tableWrapper?.removeEventListener('scroll', this.moveActions)
    },
    moveActions() {
      if (!this.tableWrapper) return

      const { scrollLeft } = this.tableWrapper
      if (scrollLeft !== this.actionsOffset.scrolled) {
        const { tableVisibleArea, tableWidth } = this.actionsOffset
        this.actionsOffset.scrolled =
          scrollLeft + tableVisibleArea < tableWidth ? scrollLeft : tableWidth - tableVisibleArea
      }
    },
    updateTableActionsOffset() {
      const { tableWrapper } = this
      if (!tableWrapper) return

      this.actionsOffset.tableVisibleArea = tableWrapper.clientWidth
      this.actionsOffset.tableWidth = tableWrapper.scrollWidth
      this.moveActions()
    },
    resizeTable() {
      this.updateTableActionsOffset()
    },
    refreshTableActionsOffsetOnLayoutChange() {
      if (!this.tableActionsOffsetActive) return

      this.$nextTick(() => {
        if (!this.tableActionsOffsetActive) return

        this.setupResizeObserver()
        this.updateTableActionsOffset()
      })
    }
  }
}
</script>
